import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { APP, ROUTES, isPublicHost, pickPostLoginRoute, resolveApexDomain, sanitiseOrgCode, workspaceHostUrl } from '../config';
import { useAuth } from '../lib/auth';
import { ErrorBox } from '../components/Loader.jsx';
import { canonicalizeLoginIdentifier, looksLikeEmail } from '../lib/loginIdentifier';
import { fetchLoginSecurityConfig, getDeviceId } from '../lib/loginSecurity';

const SIGNUP_PATH = '/super_admin/signup';
const ORGANISATION_CODE_HINT = 'Enter Organisation Code eg: Apple';

function privacyPolicyUrl() {
  if (isPublicHost()) return '/copyright';
  const apex = resolveApexDomain();
  const { protocol, port } = window.location;
  const portSuffix = port ? `:${port}` : '';
  return `${protocol}//${apex}${portSuffix}/copyright`;
}

export default function LoginPage() {
  const { login, isAuthenticated, bootstrapping, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const onApex = isPublicHost();

  const [organisation, setOrganisation] = useState('');
  const [orgError, setOrgError] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [security, setSecurity] = useState({
    honeypot_enabled: false,
    timing_check_enabled: false,
    device_limit_enabled: false,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cfg = await fetchLoginSecurityConfig();
      if (!cancelled) setSecurity(cfg);
      if (cfg.device_limit_enabled) {
        await getDeviceId(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (bootstrapping) {
    return (
      <div className="ty-fullscreen-center">
        <div className="ty-spinner" aria-label="Loading" />
      </div>
    );
  }
  if (isAuthenticated) {
    return <Navigate to={pickPostLoginRoute(user, location)} replace />;
  }

  const onContinueToWorkspace = (e) => {
    e.preventDefault();
    const slug = sanitiseOrgCode(organisation);
    if (!slug) {
      setOrgError(ORGANISATION_CODE_HINT);
      return;
    }
    setOrgError(null);
    const params = new URLSearchParams(location.search || '');
    let next = params.get('next') || '';
    if (!next) {
      const stores = new URLSearchParams();
      if (params.get('connect_code') || params.get('code')) {
        stores.set('connect_code', params.get('connect_code') || params.get('code'));
      }
      if (params.get('shop')) stores.set('shop', params.get('shop'));
      if (stores.toString()) next = `/signalbridge/stores?${stores.toString()}`;
    }
    const path = (next && next.startsWith('/') && !next.startsWith('//'))
      ? `/login?next=${encodeURIComponent(next)}`
      : '/login';
    window.location.assign(workspaceHostUrl(slug, path));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const loggedInUser = await login(canonicalizeLoginIdentifier(email), password);
      navigate(pickPostLoginRoute(loggedInUser, location), { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const passwordRecoveryTo = (() => {
    const params = new URLSearchParams();
    if (looksLikeEmail(email)) params.set('email', canonicalizeLoginIdentifier(email));
    if (onApex && organisation.trim()) params.set('organisation', organisation.trim());
    const search = params.toString();
    return search ? `${ROUTES.PASSWORD_RECOVERY}?${search}` : ROUTES.PASSWORD_RECOVERY;
  })();

  return (
    <div className="ty-login">
      <div className="ty-login__card">
        <div
          aria-hidden
          style={{
            width: 64,
            height: 64,
            margin: '0 auto 14px',
            borderRadius: 18,
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
            fontSize: 28,
            fontWeight: 800,
            letterSpacing: '-0.02em',
            background: 'linear-gradient(135deg, #7980ff 0%, #656cfd 100%)',
            boxShadow: '0 12px 28px rgba(121, 128, 255, 0.35)',
          }}
        >
          {(APP.name || 'T').slice(0, 1).toUpperCase()}
        </div>
        <h1 className="ty-login__title">Login / Sign up</h1>
        <p className="ty-login__sub">
          {onApex
            ? 'Sign in to a workspace, create an organisation, or use a personal account on this host.'
            : APP.tagline}
        </p>

        {onApex ? (
          <form onSubmit={onContinueToWorkspace}>
            <div className="ty-field">
              <label htmlFor="organisation">Organisation Code</label>
              <input
                id="organisation"
                className="ty-input"
                type="text"
                autoComplete="organization"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                value={organisation}
                onChange={(e) => {
                  setOrganisation(e.target.value);
                  if (orgError) setOrgError(null);
                }}
                placeholder={ORGANISATION_CODE_HINT}
              />
            </div>
            {orgError ? (
              <p className="ty-login__sub" role="alert" style={{ marginBottom: 12, color: '#c2410c' }}>
                {orgError}
              </p>
            ) : null}
            <button className="ty-btn" type="submit" style={{ width: '100%' }}>
              Continue to workspace
            </button>
          </form>
        ) : null}

        <form onSubmit={onSubmit} style={onApex ? { marginTop: 28 } : undefined}>
          {onApex ? (
            <p className="ty-login__sub" style={{ marginBottom: 16 }}>
              Already on this host? Sign in with email, username, or phone and password.
            </p>
          ) : null}

          <ErrorBox error={error} />

          <div className="ty-field">
            <label htmlFor="email">Email, username, or phone</label>
            <input
              id="email"
              className="ty-input"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Email, username, or phone number"
            />
          </div>

          <div className="ty-field">
            <div className="ty-login__pwhead">
              <label htmlFor="password">Password</label>
              <Link to={passwordRecoveryTo} className="ty-link ty-login__forgot">
                Forgot password?
              </Link>
            </div>
            <input
              id="password"
              className="ty-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {security.honeypot_enabled ? (
            <div
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', height: 0, overflow: 'hidden' }}
            >
              <label htmlFor="website_url">Website URL</label>
              <input
                id="website_url"
                name="website_url"
                type="text"
                autoComplete="off"
                tabIndex={-1}
                defaultValue=""
              />
            </div>
          ) : null}

          <button className="ty-btn" type="submit" disabled={submitting} style={{ width: '100%' }}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {onApex ? (
          <p style={{ marginTop: 20, textAlign: 'center', fontSize: 14 }}>
            Working on your own?{' '}
            <Link to={ROUTES.SIGNUP} className="ty-link">
              Sign up as an individual
            </Link>
          </p>
        ) : null}

        {onApex ? (
          <p style={{ marginTop: 12, textAlign: 'center', fontSize: 14 }}>
            Need a new workspace?{' '}
            <a href={SIGNUP_PATH} className="ty-link">
              Start free — create your workspace
            </a>
          </p>
        ) : null}

        {onApex ? (
          <p style={{ marginTop: 12, textAlign: 'center', fontSize: 14 }}>
            Platform operator?{' '}
            <Link to="/super_admin/login" className="ty-link">
              Super-admin login
            </Link>
          </p>
        ) : null}

        <p style={{ marginTop: 16, textAlign: 'center', fontSize: 13 }}>
          <a href={privacyPolicyUrl()} className="ty-link">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
}
